import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";
// TawkChat hidden per user request
// import TawkChat from "./components/TawkChat";

// Pages
import LandingPage from "./pages/LandingPage";
import ProductCatalog from "./pages/ProductCatalog";
import Login from "./pages/Login";
import Register from "./pages/Register";

// Admin Pages
import Dashboard from "./pages/admin/Dashboard";
import Orders from "./pages/admin/Orders";
import CustomOrders from "./pages/admin/CustomOrders";
import Reports from "./pages/admin/Reports";
import ProductMaster from "./pages/admin/ProductMaster";
import CustomerMaster from "./pages/admin/CustomerMaster";
import WarehouseMaster from "./pages/admin/WarehouseMaster";
import RefundLog from "./pages/admin/RefundLog";
import UserManagement from "./pages/admin/UserManagement";
import DataManagement from "./pages/admin/DataManagement";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import EmailActivity from "./pages/admin/EmailActivity";
import PromoCodes from "./pages/admin/PromoCodes";
import AdminSettings from "./pages/admin/AdminSettings";

// Customer Pages
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import CustomerOrders from "./pages/customer/CustomerOrders";
import NewOrder from "./pages/customer/NewOrder";
import Subscriptions from "./pages/customer/Subscriptions";
import TrackOrder from "./pages/customer/TrackOrder";
import CheckoutSuccess from "./pages/customer/CheckoutSuccess";
import CustomerInvoices from "./pages/customer/CustomerInvoices";
import Profile from "./pages/customer/Profile";

// Delivery Pages
import DeliveryDashboard from "./pages/delivery/DeliveryDashboard";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireDelivery = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.role !== 'admin') {
    if (user.role === 'delivery') return <Navigate to="/delivery" replace />;
    return <Navigate to="/customer/dashboard" replace />;
  }

  if (requireDelivery && user.role !== 'delivery') {
    if (user.role === 'admin') return <Navigate to="/dashboard" replace />;
    return <Navigate to="/customer/dashboard" replace />;
  }

  // Block delivery users from customer routes (non-admin, non-delivery routes)
  if (!requireAdmin && !requireDelivery && user.role === 'delivery') {
    return <Navigate to="/delivery" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/catalog" element={<ProductCatalog />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Admin Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireAdmin>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute requireAdmin>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/custom-orders"
        element={
          <ProtectedRoute requireAdmin>
            <CustomOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute requireAdmin>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-data/products"
        element={
          <ProtectedRoute requireAdmin>
            <ProductMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-data/customers"
        element={
          <ProtectedRoute requireAdmin>
            <CustomerMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-data/warehouses"
        element={
          <ProtectedRoute requireAdmin>
            <WarehouseMaster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/refund-log"
        element={
          <ProtectedRoute requireAdmin>
            <RefundLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute requireAdmin>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-management"
        element={
          <ProtectedRoute requireAdmin>
            <DataManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute requireAdmin>
            <AdminSubscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-activity"
        element={
          <ProtectedRoute requireAdmin>
            <EmailActivity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/promo-codes"
        element={
          <ProtectedRoute requireAdmin>
            <PromoCodes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requireAdmin>
            <AdminSettings />
          </ProtectedRoute>
        }
      />

      {/* Customer Routes */}
      <Route
        path="/customer/dashboard"
        element={
          <ProtectedRoute>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/orders"
        element={
          <ProtectedRoute>
            <CustomerOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/new-order"
        element={
          <ProtectedRoute>
            <NewOrder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/subscriptions"
        element={
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/invoices"
        element={
          <ProtectedRoute>
            <CustomerInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/track"
        element={
          <ProtectedRoute>
            <TrackOrder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customer/checkout-success"
        element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customer/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />


      {/* Delivery Routes */}
      <Route
        path="/delivery"
        element={
          <ProtectedRoute requireDelivery>
            <DeliveryDashboard />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import InstallPWA from "./components/InstallPWA";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster />
        <InstallPWA />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
